import { SnapSelectMenuOption } from "@snap-mobile/snap-ui/dist/types/utils";
import {
  SpendAccountLimitsResponse,
  SpendBankAccount,
  SpendExternalTransferFee,
  useSpendGroupBankAccountsLazyQuery,
  useSpendOrganizationBankAccountsLazyQuery,
} from "graphql/generated";
import { useAppContexts } from "hooks/use-context";
import { useEffect, useState } from "react";
import { PlaidLinkOptions, usePlaidLink } from "react-plaid-link";
import HasAccount from "shared-components/banking/transfer/has-account";
import NoAccount from "shared-components/banking/transfer/no-account";
import { SpinnerContainer } from "shared-components/spinner";
import { SnapSelectMenu } from "suit";
import { transferError } from "types/errors";

type ExternalProps = {
  to: SnapSelectMenuOption[];
  amount: string;
  hasErrorObj: transferError;
  limits?: SpendAccountLimitsResponse;
  externalTransferFee?: SpendExternalTransferFee | null;
  feeAmount: string;
  selectedItem: SnapSelectMenuOption | undefined;
  setAmount: React.Dispatch<React.SetStateAction<string>>;
  setNote: React.Dispatch<React.SetStateAction<string>>;
  setSelectedAccountId: React.Dispatch<React.SetStateAction<string>>;
  setDirection: React.Dispatch<React.SetStateAction<string>>;
  setHasErrorObj: React.Dispatch<React.SetStateAction<transferError>>;
  setExternalTo: React.Dispatch<React.SetStateAction<string>>;
  setExternalFrom: React.Dispatch<React.SetStateAction<string>>;
  setFeeAmount: React.Dispatch<React.SetStateAction<string>>;
  setSelectedItem: React.Dispatch<
    React.SetStateAction<SnapSelectMenuOption | undefined>
  >;
};

function External({
  to,
  amount,
  hasErrorObj,
  limits,
  externalTransferFee,
  feeAmount,
  selectedItem,
  setAmount,
  setNote,
  setSelectedAccountId,
  setDirection,
  setHasErrorObj,
  setExternalTo,
  setExternalFrom,
  setFeeAmount,
  setSelectedItem,
}: ExternalProps) {
  const { program, group } = useAppContexts();
  const [selectedOption, setSelectedOption] = useState("Transfer OUT");
  const [linkedAccounts, setLinkedAccounts] = useState<SpendBankAccount[]>([]);

  const [
    getOrgBankAccount,
    { loading: loadingOrgBankData, data: orgBankData },
  ] = useSpendOrganizationBankAccountsLazyQuery();
  const [
    getGroupBankAccount,
    { loading: loadingGroupBankData, data: groupBankData },
  ] = useSpendGroupBankAccountsLazyQuery();

  useEffect(() => {
    if (
      !loadingOrgBankData &&
      orgBankData &&
      orgBankData.spendOrganizationBankAccounts
    ) {
      setLinkedAccounts(
        orgBankData.spendOrganizationBankAccounts
          .externalAccounts as SpendBankAccount[]
      );
    }
  }, [loadingOrgBankData, orgBankData]);
  useEffect(() => {
    if (
      !loadingGroupBankData &&
      groupBankData &&
      groupBankData.spendGroupBankAccounts
    ) {
      setLinkedAccounts(
        groupBankData.spendGroupBankAccounts
          .externalAccounts as SpendBankAccount[]
      );
    }
  }, [loadingGroupBankData, groupBankData]);

  const [plaidConfig, setPlaidConfig] = useState<PlaidLinkOptions>({
    onSuccess: (public_token, metadata) => {
      console.log("HIT SUCCESS");
    },
    onExit: (err, metadata) => {},
    onEvent: (eventName, metadata) => {},
    token: "",
  });
  const { open, ready } = usePlaidLink(plaidConfig);
  const openPlaid = () => {
    if (ready) {
      open();
    }
  };

  return (
    <div className="modal-card min-h-80">
      <h1>Select an Account</h1>
      <SnapSelectMenu
        selectMenuOptions={to}
        className="lg:w-[50%] mb-5"
        onSnap-select-menu-updated={(e) => {
          const selectedItem = e.detail.find((e) => e.selected);
          setSelectedItem(selectedItem);
          if (selectedItem?.value && program?.is(selectedItem?.value)) {
            getOrgBankAccount({
              fetchPolicy: "network-only",
            });
          } else if (selectedItem?.value && group?.is(selectedItem?.value)) {
            getGroupBankAccount({
              variables: {
                groupId: selectedItem.value,
              },
              fetchPolicy: "network-only",
            });
          }
        }}
      />
      {selectedItem && (
        <>
          {loadingOrgBankData ||
            (loadingGroupBankData && <SpinnerContainer loading={false} />)}
          {!loadingOrgBankData &&
            !loadingGroupBankData &&
            linkedAccounts.length > 0 && (
              <HasAccount
                setSelectedOption={setSelectedOption}
                selectedOption={selectedOption}
                amount={amount}
                linkedAccounts={linkedAccounts}
                despotAccount={selectedItem.name}
                setAmount={setAmount}
                setNote={setNote}
                setSelectedAccountId={setSelectedAccountId}
                setDirection={setDirection}
                hasErrorsObj={hasErrorObj}
                setHasErrorObj={setHasErrorObj}
                setExternalTo={setExternalTo}
                setExternalFrom={setExternalFrom}
                limits={limits}
                externalTransferFee={externalTransferFee}
                feeAmount={feeAmount}
                setFeeAmount={setFeeAmount}
              />
            )}
          {!loadingOrgBankData &&
            !loadingGroupBankData &&
            !linkedAccounts.length && (
              <NoAccount
                openPlaid={openPlaid}
                setPlaidConfig={setPlaidConfig}
                type={program?.is(selectedItem.value ?? "") ? "program" : "group"}
                groupId={selectedItem.value ?? ""}
              />
            )}
        </>
      )}
    </div>
  );
}

export default External;
